<template>
  <div style="width: 100%; height: 100%;">
    <div
      v-if="chartLoader"
      class="loader-container"
    >
      <v-progress-circular
        indeterminate
        color="green"
        width="2"
        size="38"
      />
    </div>
    <BarChart
      v-else
      :chart-data="chartData"
      :options="options"
      :styles="styles"
      @chart:rendered="$emit('stop-loader')"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { BarChart } from '@/components/charts'
import { getRelativeDates } from '@/utils/time'
import { setCommas } from '@/utils/helpers.js'

export default {
  name: 'BarLineChart',
  components: {
    BarChart
  },
  props: {
    dailyVisits: {
      type: Array,
      required: true
    },
    dailyAmount: {
      type: Array,
      required: true
    },
    loader: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      colors: {
        line: {
          border: '#13bb3d',
          background: 'rgba(19, 187, 61, 0.2)'
        },
        barAlpha: {
          today: 0.85,
          past: 0.3,
          future: 0.6
        }
      },
      styles: {
        height: '100%',
        width: '100%',
        position: 'relative'
      }
    }
  },
  computed: {
    ...mapState('dashboard', ['chartLoader']),
    dates () {
      const length = this.dailyVisits.length
      if (!length) return []
      return getRelativeDates(length, -length, length <= 14)
    },
    options () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          rtl: true
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false
            }
          }],
          yAxes: [{
            id: 'B',
            position: 'left',
            ticks: {
              callback: function (value) {
                return value + '件'
              },
              beginAtZero: true,
              stepSize: 1,
              min: 0,
              // if max value === 56 { max: 70} (min max = 10)
              max: Math.ceil(Math.max(...this.dailyVisits, 1) / 10) * 10,
              maxTicksLimit: 10
            }
          },
          {
            id: 'A',
            position: 'right',
            gridLines: {
              display: false
            },
            ticks: {
              callback: function (value) {
                // 4桁以上の場合下3桁削除、それでも4桁以上行く場合コンマを追加。
                const arr = setCommas(value).split(',')
                const isBiggerThanThousand = arr.length >= 2
                let last = ''
                if (isBiggerThanThousand) {
                  const p = arr.pop()
                  if (p !== '000') last = `.${p.charAt(0)}`
                }
                const str = arr.join(',')
                // 1000以上の場合"k"を付与する. 上で削除した下3桁が0以外の場合その1桁目を追加。 ("500"の場合".5")
                return isBiggerThanThousand ? str + last + 'kℓ' : str + 'ℓ'
              },
              beginAtZero: true,
              stepSize: 50,
              min: 0,
              // if max value === 4567 { max: 5000} (min max = 1000)
              max: Math.ceil(Math.max(...this.dailyAmount, 10) / 1000) * 1000,
              maxTicksLimit: 10
            }
          }]
        },
        tooltips: {
          mode: 'index',
          xPadding: 20,
          yPadding: 10,
          cornerRadius: 33,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          borderColor: 'rgba(0,0,0,0.4)',
          borderWidth: 1,
          titleFontColor: '#333',
          bodyFontSize: 20,
          bodyFontColor: '#333',
          callbacks: {
            label: ({ value, datasetIndex }) => {
              if (datasetIndex === 0) {
                return `${value}件`
              }
              if (datasetIndex === 1) {
                return `${setCommas(value)}ℓ`
              }
            }
          },
          itemSort: (a, b) => a.datasetIndex - b.datasetIndex
        }
      }
    },
    chartData () {
      return {
        labels: this.dates,
        datasets: [
          { // Bar Chart
            label: '配送件数',
            order: 1,
            yAxisID: 'B',
            data: this.dailyVisits,
            backgroundColor: `rgba(255, 160, 20, ${this.colors.barAlpha.today})`,
            borderWidth: 2,
            hoverBorderWidth: 10
          },
          { // Line Chart
            type: 'line',
            label: '給油量',
            order: 0,
            yAxisID: 'A',
            data: this.dailyAmount,
            lineTension: 0.1,
            pointBorderWidth: 3,
            pointHoverBorderWidth: 5,
            borderColor: this.colors.line.border,
            backgroundColor: this.colors.line.background
          }
        ]
      }
    }
  },
  methods: {
    getDataByIndex (arr, ...index) {
      return arr.map((el, i) => {
        return index.some(idx => i === idx)
          ? el
          : 0
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.loader-container{
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
}
</style>
